import React, { useState, useCallback } from 'react';
import { SmilePlus, ChevronDown, Lightbulb, Loader } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '../../../components/ui/dropdown-menu';
import type { GlobalDialogProps, FolderType } from '../../../types';
import { createFolder } from '../librarySlice';
import type { AppDispatch } from '../../../store';
import { RootState } from '../../../reducers';
import { AVAILABLE_ICONS } from './actions/FolderIcon';
import useUserTags from '../../../hooks/useTags';
import { FOLDER_SUGGESTIONS } from './folderSuggestions';

const MAX_FOLDER_NAME_LENGTH = 50;
const MIN_FOLDER_NAME_LENGTH = 1;

export default function CreateFolderDialog({ onClose }: GlobalDialogProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { setTag, getTag } = useUserTags();
  const { status } = useSelector((state: RootState) => state.library.folders);
  const [folderName, setFolderName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('Folder');
  const [error, setError] = useState('');
  const isLoading = status === 'pending';

  const SelectedIconComponent =
    AVAILABLE_ICONS.find((icon) => icon.name === selectedIcon)?.icon ||
    AVAILABLE_ICONS[0].icon;

  const validateFolderName = useCallback(
    (name: string) => {
      const trimmedName = name.trim();
      if (trimmedName.length < MIN_FOLDER_NAME_LENGTH) {
        setError('');
        return false;
      }
      if (trimmedName.length > MAX_FOLDER_NAME_LENGTH) {
        setError(
          t('library.folder.create.errors.maxLength', {
            max: MAX_FOLDER_NAME_LENGTH,
          }),
        );
        return false;
      }
      if (!/^[a-zA-Z0-9\s-_()]+$/.test(trimmedName)) {
        setError(t('library.folder.create.errors.invalidChars'));
        return false;
      }
      setError('');
      return true;
    },
    [t],
  );

  const handleFolderNameChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(value);
    validateFolderName(value);
  };

  const handleCreateFolder = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const trimmedName = folderName.trim();
    if (!validateFolderName(trimmedName) || isLoading) {
      return;
    }

    try {
      // Dispatch the create folder action
      const resultAction = await dispatch(createFolder(trimmedName));

      // Type guard to check if the action was fulfilled
      if ('payload' in resultAction && resultAction.payload) {
        const newFolder = resultAction.payload as FolderType;

        // If the folder was created successfully, save the icon
        if (newFolder && newFolder._id && selectedIcon !== 'Folder') {
          const folderIconsTag = getTag('folder_icons');
          const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};
          const updatedIcons = {
            ...folderIcons,
            [newFolder._id]: selectedIcon,
          };
          setTag({
            key: 'folder_icons',
            value: JSON.stringify(updatedIcons),
          });
        }

        onClose();
      }
    } catch (err) {
      // Handle error if needed
      console.error('Error creating folder:', err);
      // Keep dialog open on error so user can retry
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && folderName.trim() && !error && !isLoading) {
      handleCreateFolder();
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('library.folder.create.title', 'Create New Folder')}
          </DialogTitle>
        </DialogHeader>

        {/* Info banner */}
        <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/50">
          <Lightbulb className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            Folders keep recordings organized in one place. Use them for ongoing
            projects, or just to keep things tidy.
          </p>
        </div>

        <form onSubmit={handleCreateFolder} className="space-y-4">
          {/* Input field with icon selector */}
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 hover:bg-accent"
                    >
                      {selectedIcon === 'Folder' ? (
                        <SmilePlus className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <SelectedIconComponent className="h-4 w-4" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[280px] p-2"
                    sideOffset={5}
                  >
                    <div className="grid grid-cols-7 gap-1">
                      {AVAILABLE_ICONS.map(({ name, icon: Icon }) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setSelectedIcon(name)}
                          className={`
                            flex items-center justify-center p-2 rounded-md transition-colors
                            ${
                              selectedIcon === name
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-accent hover:text-accent-foreground'
                            }
                          `}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Input
                id="folder-name"
                value={folderName}
                onChange={handleFolderNameChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoFocus
                maxLength={MAX_FOLDER_NAME_LENGTH}
                aria-label={t('library.folder.create.ariaLabel', 'Folder name')}
                data-testid="folder-name-input"
                className={`pl-11 pr-2 ${error ? 'border-destructive' : ''}`}
                placeholder={t(
                  'library.folder.create.placeholder',
                  'Enter folder name...',
                )}
              />
            </div>

            {/* Suggestions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-48 justify-between h-10"
                >
                  Quick suggestions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                {FOLDER_SUGGESTIONS.map((suggestion) => {
                  const SuggestionIcon =
                    AVAILABLE_ICONS.find(
                      (icon) => icon.name === suggestion.icon,
                    )?.icon || AVAILABLE_ICONS[0].icon;

                  return (
                    <DropdownMenuItem
                      key={suggestion.name}
                      onClick={() => {
                        setFolderName(suggestion.name);
                        setSelectedIcon(suggestion.icon);
                        validateFolderName(suggestion.name);
                      }}
                      className="cursor-pointer"
                    >
                      <SuggestionIcon className="mr-2 h-4 w-4" />
                      <span>{suggestion.name}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </form>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-3 sm:gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            data-testid="cancel-create-folder-button"
            className="min-w-[100px]"
          >
            {t('library.folder.create.buttons.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => handleCreateFolder()}
            disabled={!folderName.trim() || !!error || isLoading}
            data-testid="create-folder-submit-button"
            className="min-w-[120px]"
          >
            {isLoading ? (
              <div className="flex items-center gap-2 justify-center">
                <Loader className="h-4 w-4 animate-spin text-current" />
                {t('library.folder.create.buttons.creating', 'Creating...')}
              </div>
            ) : (
              t('library.folder.create.buttons.create', 'Create Folder')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
